import { useEffect, useRef } from "react";

/**
 * Configuration for a query param to preserve across client-side navigations.
 */
export interface PreservedParam {
  /** The URL query param name (e.g., "impersonate", "org") */
  name: string;
  /** sessionStorage key used to bridge the value across navigations */
  storageKey: string;
}

const SESSION_PREFIX = "__preserved_param_";

/**
 * Preserves specified URL query params across client-side navigations.
 *
 * Patches window.history.pushState/replaceState to inject preserved params
 * into destination URLs. This catches all navigation paths (Next.js router,
 * <Link>, programmatic pushState) without relying on React lifecycle events.
 *
 * On full page load, the URL is the source of truth — if a param is absent,
 * its stale sessionStorage value is cleared. During client-side navigations,
 * sessionStorage bridges the value across pushState/replaceState calls.
 *
 * A new tab is a full page load, so it only keeps the params if its URL has
 * them. window.open is patched to add them to same-origin URLs, and a
 * capturing listener adds them to a same-origin <a href> before anything can
 * open it (pointer/mouse down, middle-click, context menu, keyboard focus or
 * Enter, click), covering target="_blank", modifier clicks, "Open in new tab"
 * and copy-link.
 *
 * Must be called once at _app.tsx level so it runs before any navigation.
 *
 * @example
 * // In _app.tsx:
 * usePreserveQueryParams([
 *   { name: "impersonate", storageKey: "impersonate" },
 *   { name: "org", storageKey: "org" },
 * ]);
 */
export function usePreserveQueryParams(params: PreservedParam[]): void {
  // Use a ref so the patch closures always see the latest param list
  // without needing to re-register the patches on every render.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    // On full page load, URL is the source of truth.
    // Store present params; clear stale ones.
    const currentSearch = new URLSearchParams(window.location.search);
    for (const param of paramsRef.current) {
      const key = SESSION_PREFIX + param.storageKey;
      const value = currentSearch.get(param.name);
      if (value) {
        sessionStorage.setItem(key, value);
      } else {
        sessionStorage.removeItem(key);
      }
    }

    const origPushState = window.history.pushState.bind(window.history);
    const origReplaceState = window.history.replaceState.bind(window.history);

    /** Adds missing preserved params to urlObj. Returns whether it changed. */
    function addStoredParams(urlObj: URL): boolean {
      let modified = false;
      for (const param of paramsRef.current) {
        if (!urlObj.searchParams.has(param.name)) {
          const stored = sessionStorage.getItem(
            SESSION_PREFIX + param.storageKey,
          );
          if (stored) {
            urlObj.searchParams.set(param.name, stored);
            modified = true;
          }
        }
      }
      return modified;
    }

    function injectParams(
      url: string | URL | null | undefined,
    ): string | URL | null | undefined {
      if (!url) return url;

      try {
        const urlObj = new URL(
          typeof url === "string" ? url : url.toString(),
          window.location.origin,
        );
        if (addStoredParams(urlObj)) {
          return urlObj.pathname + urlObj.search + urlObj.hash;
        }
      } catch {
        // not a valid URL, pass through
      }
      return url;
    }

    /**
     * Returns url with the preserved params added when it points at this
     * app (same origin, http/https), or null to leave it unchanged.
     */
    function withParamsForNewDocument(url: string | URL): string | null {
      const raw = url.toString();
      // Hash-only links stay on the current document.
      if (raw === "" || raw.startsWith("#")) return null;
      try {
        const urlObj = new URL(raw, window.location.href);
        if (
          urlObj.origin !== window.location.origin ||
          (urlObj.protocol !== "http:" && urlObj.protocol !== "https:")
        ) {
          return null;
        }
        return addStoredParams(urlObj) ? urlObj.href : null;
      } catch {
        return null;
      }
    }

    function syncFromUrl(url: string | URL | null | undefined): void {
      if (!url) return;
      try {
        const urlObj = new URL(
          typeof url === "string" ? url : url.toString(),
          window.location.origin,
        );
        for (const param of paramsRef.current) {
          const val = urlObj.searchParams.get(param.name);
          if (val) {
            sessionStorage.setItem(SESSION_PREFIX + param.storageKey, val);
          }
        }
      } catch {
        // ignore
      }
    }

    window.history.pushState = function (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any,
      unused: string,
      url?: string | URL | null,
    ) {
      return origPushState(data, unused, injectParams(url));
    };

    window.history.replaceState = function (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: any,
      unused: string,
      url?: string | URL | null,
    ) {
      // If the URL explicitly sets any of our params, sync to sessionStorage
      // and pass through without injection (avoid overwriting explicit values).
      syncFromUrl(url);
      return origReplaceState(data, unused, injectParams(url));
    };

    const origOpen = window.open;
    window.open = function (
      url?: string | URL,
      target?: string,
      features?: string,
    ) {
      const withParams = url ? withParamsForNewDocument(url) : null;
      return origOpen.call(window, withParams ?? url, target, features);
    };

    // Rewrite a same-origin link's href before it can be opened, so a new
    // tab (target="_blank", Cmd/Ctrl/middle-click, "Open in new tab") or a
    // copied link carries the params. Capturing so it runs before handlers
    // that might stop propagation.
    function rewriteAnchorHref(event: Event): void {
      if (event instanceof KeyboardEvent && event.key !== "Enter") return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      // An editor (ProseMirror) would read the rewrite back into saved content.
      if (anchor.isContentEditable) return;
      const href = anchor.getAttribute("href");
      if (href === null) return;
      const withParams = withParamsForNewDocument(href);
      if (withParams) anchor.setAttribute("href", withParams);
    }
    const anchorEvents = [
      "pointerdown",
      "mousedown",
      "auxclick",
      "contextmenu",
      "focusin",
      "keydown",
      "click",
    ];
    for (const type of anchorEvents) {
      document.addEventListener(type, rewriteAnchorHref, true);
    }

    return () => {
      window.history.pushState = origPushState;
      window.history.replaceState = origReplaceState;
      window.open = origOpen;
      for (const type of anchorEvents) {
        document.removeEventListener(type, rewriteAnchorHref, true);
      }
    };
  }, []);
}

/**
 * Clear a preserved param from sessionStorage.
 * Call when the param should no longer be preserved (e.g., stopping impersonation).
 */
export function clearPreservedParam(storageKey: string): void {
  sessionStorage.removeItem(SESSION_PREFIX + storageKey);
}

/** Read a preserved param's stored value, or null. */
export function getPreservedParam(storageKey: string): string | null {
  try {
    return sessionStorage.getItem(SESSION_PREFIX + storageKey);
  } catch {
    return null;
  }
}
